import { useState, useEffect } from 'react';
import {
  Plus,
  Download,
  FileSpreadsheet,
  Sparkles,
  MessageSquare,
  CalendarClock,
} from 'lucide-react';
import { format } from 'date-fns';
import { taskApi } from '@/shared/services/taskApi.ts';
import { CreateTaskModal } from './CreateTaskModal';
import { TaskDetailsModal } from './TaskDetailsModal';
import { ExportJiraModal } from './ExportJiraModal';
import { api } from '@/shared/services/api.ts';
import toast from 'react-hot-toast';
import { Task, TaskStatus, TaskPriority } from '@/types/task.ts';
import {
  taskStatusMeta,
  taskStatusOrder,
  taskPriorityMeta,
  taskTypeMeta,
} from '@/shared/lib/classificationMeta';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { cn } from '@/shared/utils/cn';
import { downloadBlob } from '@/shared/utils/download';
import { getInitials } from '@/shared/utils/user';

const PRIORITY_SEGMENTS = 5;

/** Monochrome 1–5 level glyph — priority intensity by fill, never by color. */
const PriorityBars = ({ priority }: { priority: TaskPriority }) => {
  const meta = taskPriorityMeta[priority];
  return (
    <span
      className="flex items-center gap-0.5 text-muted-foreground"
      title={`Priority: ${meta.label}`}
      aria-label={`Priority: ${meta.label} (${meta.level} of ${PRIORITY_SEGMENTS})`}
    >
      {Array.from({ length: PRIORITY_SEGMENTS }).map((_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={cn(
            'h-2.5 w-1 rounded-[1px] bg-current',
            i < meta.level ? 'opacity-100' : 'opacity-20'
          )}
        />
      ))}
    </span>
  );
};

export function TaskBoard() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<TaskStatus | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isExportJiraModalOpen, setIsExportJiraModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const data = await taskApi.getAllTasks();
      setTasks(data);
    } catch (err) {
      setError('Failed to load tasks');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (task: Task) => {
    setDraggedTask(task);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (status: TaskStatus) => {
    setDragOverStatus(null);
    if (!draggedTask || draggedTask.status === status) {
      setDraggedTask(null);
      return;
    }

    try {
      await taskApi.updateTaskStatus(draggedTask.id, status);
      setTasks(prev =>
        prev.map(t =>
          t.id === draggedTask.id ? { ...t, status } : t
        )
      );
    } catch (err) {
      console.error('Failed to update task status:', err);
    } finally {
      setDraggedTask(null);
    }
  };

  const getTasksByStatus = (status: TaskStatus) => {
    return tasks.filter(task => task.status === status);
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);
    try {
      const response = await api.get('/tasks/export/excel', {
        responseType: 'blob'
      });

      downloadBlob(response.data, `tasks_export_${Date.now()}.xlsx`);

      toast.success('Tasks exported to Excel successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export tasks to Excel');
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-danger/30 bg-danger-subtle px-4 py-3 text-sm text-danger-foreground">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Task Board</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Manage your tasks with drag &amp; drop
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleExportToExcel}
            disabled={isExporting || tasks.length === 0}
            className="btn-outline btn-sm gap-2"
            title="Export all tasks to Excel"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Excel
          </button>
          <button
            onClick={() => setIsExportJiraModalOpen(true)}
            disabled={tasks.length === 0}
            className="btn-outline btn-sm gap-2"
            title="Export tasks to Jira format"
          >
            <Download className="h-4 w-4" />
            Jira
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn-primary gap-2"
          >
            <Plus className="h-4 w-4" />
            Create Task
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="overflow-x-auto pb-2">
        <div className="flex gap-4">
          {taskStatusOrder.map((status) => {
            const meta = taskStatusMeta[status];
            const columnTasks = getTasksByStatus(status);
            const isDropTarget = dragOverStatus === status;

            return (
              <div
                key={status}
                onDragOver={(e) => {
                  handleDragOver(e);
                  if (draggedTask) setDragOverStatus(status);
                }}
                onDragLeave={() => setDragOverStatus((s) => (s === status ? null : s))}
                onDrop={() => handleDrop(status)}
                className={cn(
                  'flex w-80 shrink-0 flex-col rounded-xl border border-border bg-surface transition-colors',
                  isDropTarget && 'bg-primary/5 ring-2 ring-primary'
                )}
              >
                {/* Column Header */}
                <div className="sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-xl border-b border-border bg-surface px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className={cn('h-2.5 w-2.5 rounded-full', meta.dot)} aria-hidden="true" />
                    <h2 className="text-sm font-semibold text-foreground">{meta.label}</h2>
                  </div>
                  <span className="badge-neutral tabular-nums">{columnTasks.length}</span>
                </div>

                {/* Tasks */}
                <div className="flex min-h-[240px] flex-col gap-3 p-3">
                  {columnTasks.length === 0 ? (
                    <EmptyState
                      icon={taskTypeMeta.Task.icon}
                      title="No tasks"
                      description={
                        isDropTarget ? 'Drop here to move' : `Nothing in ${meta.label.toLowerCase()}`
                      }
                    />
                  ) : (
                    columnTasks.map((task) => {
                      const TypeIcon = taskTypeMeta[task.type].icon;
                      const isOverdue =
                        !!task.dueDate &&
                        new Date(task.dueDate).getTime() < Date.now() &&
                        task.status !== TaskStatus.Done &&
                        task.status !== TaskStatus.Cancelled;

                      return (
                        <div
                          key={task.id}
                          draggable
                          onDragStart={() => handleDragStart(task)}
                          onDragEnd={() => {
                            setDraggedTask(null);
                            setDragOverStatus(null);
                          }}
                          onClick={() => setSelectedTask(task)}
                          className={cn(
                            'card card-hover motion-safe:animate-fade-in cursor-pointer p-3',
                            draggedTask?.id === task.id && 'opacity-50'
                          )}
                        >
                          {/* Top zone: type + id + priority */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <TypeIcon className="h-3.5 w-3.5" aria-hidden="true" />
                              <span className="font-medium">
                                {taskTypeMeta[task.type].label}
                              </span>
                              <span className="text-muted-foreground/70">#{task.id}</span>
                            </div>
                            <PriorityBars priority={task.priority} />
                          </div>

                          {/* Middle zone: title */}
                          <h3 className="mt-2 line-clamp-2 text-sm font-medium text-foreground">
                            {task.title}
                          </h3>

                          {task.isAiGenerated && (
                            <span className="badge-brand mt-2">
                              <Sparkles className="h-3 w-3" />
                              AI
                            </span>
                          )}

                          {/* Labels */}
                          {task.labels.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {task.labels.slice(0, 3).map((label) => (
                                <span key={label} className="badge-neutral">
                                  {label}
                                </span>
                              ))}
                              {task.labels.length > 3 && (
                                <span className="text-xs text-muted-foreground">
                                  +{task.labels.length - 3}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Footer zone: status + assignee + meta */}
                          <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-2.5">
                            <div className="flex items-center gap-2">
                              <span
                                className={cn('h-2 w-2 rounded-full', taskStatusMeta[task.status].dot)}
                                aria-hidden="true"
                                title={taskStatusMeta[task.status].label}
                              />
                              <span
                                className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground"
                                title={`Assignee: ${task.assignedToUserName ?? 'Unassigned'}`}
                              >
                                {getInitials(task.assignedToUserName ?? '')}
                              </span>
                              {task.storyPoints != null && (
                                <span className="badge-points" title={`${task.storyPoints} story points`}>
                                  {task.storyPoints}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              {task.comments.length > 0 && (
                                <span
                                  className="flex items-center gap-1"
                                  title={`${task.comments.length} comment${task.comments.length !== 1 ? 's' : ''}`}
                                >
                                  <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                                  {task.comments.length}
                                </span>
                              )}
                              {task.dueDate && (
                                <span
                                  className={cn(
                                    'flex items-center gap-1',
                                    isOverdue && 'sev-critical rounded px-1 font-medium'
                                  )}
                                  title={isOverdue ? 'Overdue' : 'Due date'}
                                >
                                  <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
                                  {format(new Date(task.dueDate), 'MMM dd')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onTaskCreated={loadTasks}
      />

      {/* Task Details Modal */}
      <TaskDetailsModal
        isOpen={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        task={selectedTask}
        onTaskUpdated={() => {
          // Reload so the board reflects the edit, and drop the stale selection rather than
          // leaving the panel showing pre-save values (SC-262).
          setSelectedTask(null);
          loadTasks();
        }}
      />

      {/* Export to Jira Modal */}
      <ExportJiraModal
        isOpen={isExportJiraModalOpen}
        onClose={() => setIsExportJiraModalOpen(false)}
      />
    </div>
  );
}
